import { Password } from '@mui/icons-material';
import Logout from '@mui/icons-material/Logout';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import _ from 'lodash';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { logOut, selectMitarbeiterId } from '../../features/auth/api/authSlice';
import { MitarbeiterRole } from '../../features/auth/api/types';
import { useFindMitarbeiterByIdQuery } from '../../features/mitarbeiter/api/mitarbeiterApi';
import { ChangeOwnPasswordDialog } from '../../features/mitarbeiter/save/ChangeOwnPasswordDialog/ChangeOwnPasswordDialog';
import { useAppDispatch, useAppSelector } from '../../state/redux.hooks';
import { entityToDTO } from '../editUtils';
import { formatName } from '../formatUtils';
import { useNavigateDialogForm } from '../formikDialogForm/dialogForm.hook';
interface AccountMenuProps {
  extern?: boolean;
}

export default function AccountMenu({ extern }: AccountMenuProps) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const navigateToMitarbeiter = (id: string) => navigate(`/intern/mitarbeiter/${id}`);

  const mitarbeiterId = useAppSelector(selectMitarbeiterId);

  const { data } = useFindMitarbeiterByIdQuery(mitarbeiterId ?? '', { skip: !mitarbeiterId });

  const [showSetPwDialog, setShowSetPwDialog] = useState(false);
  const togglePwDialog = () => setShowSetPwDialog(p => !p);

  const navigateToEditMitarbeiter = useNavigateDialogForm(
    'mitarbeiter',
    'edit',
    data ? entityToDTO(data) : undefined
  );

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => dispatch(logOut());

  const onClickMitarbeiter = () =>
    extern ? navigateToEditMitarbeiter() : navigateToMitarbeiter(mitarbeiterId ?? '');

  const isNotConfirmed = Boolean(data && !data.confirmed);

  return (
    <React.Fragment>
      <ChangeOwnPasswordDialog
        open={isNotConfirmed || showSetPwDialog}
        variant={isNotConfirmed ? 'initial' : 'non-initial'}
        onClose={isNotConfirmed ? undefined : togglePwDialog}
      />
      <Tooltip title="Account settings">
        <IconButton onClick={handleClick} size="small" sx={{ ml: 2 }}>
          <Avatar sx={{ width: 32, height: 32 }}>{data && _.upperCase(data.email[0])}</Avatar>
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              overflow: 'visible',
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
              mt: 1.5,
              '& .MuiAvatar-root': {
                width: 32,
                height: 32,
                ml: -0.5,
                mr: 1,
              },
              '&:before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
              },
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {data && (
          <MenuItem onClick={onClickMitarbeiter}>
            <Avatar /> {formatName(data, false)}
          </MenuItem>
        )}
        {data && (
          <MenuItem disabled>
            {`${data.email}${data.role === MitarbeiterRole.ADMIN ? ' (Admin)' : ''}`}
          </MenuItem>
        )}
        <Divider />
        {data && data.role === MitarbeiterRole.EXTERN && (
          <MenuItem onClick={togglePwDialog}>
            <ListItemIcon>
              <Password fontSize="small" />
            </ListItemIcon>
            Passwort ändern
          </MenuItem>
        )}
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>
    </React.Fragment>
  );
}
